<template>
    <v-app v-if="hasPaymentFailed && !acceptAccessRoutes">
        <v-main class="background-app">
            <disabled-account />
        </v-main>
    </v-app>
    <v-app v-else-if="needUpdateApp">
        <update-app />
    </v-app>
    <v-app v-else>
        <toolbar :show-toolbar="toolbar"/>
        <navigation-drawer v-if="drawer && !isMobile" />
        <desktop-toolbar v-if="desktopToolbar" />
        <v-main class="background-app">
            <transition name="main">
                <v-container fluid :class="[{ 'no-padding': noPaddingRoutes }]">
                    <v-row no-gutters>
                        <v-col cols="12" :xl="fullWidth ? 12 : 10" :offset-xl="fullWidth ? 0 : 1">
                            <router-view></router-view>
                        </v-col>
                    </v-row>
                </v-container>
            </transition>
        </v-main>
        <bottom-navigation v-if="footer && isMobile" />
        <kimsa-toast />
        <kimsa-install-toast />
        <overlay-reconnect />
        <overlay-update />
    </v-app>
</template>

<script>
import Toolbar from "@/components/common/Toolbar";
import DesktopToolbar from "@/components/common/DesktopToolbar";
import NavigationDrawer from "@/components/common/NavigationDrawer";
import BottomNavigation from '@/components/common/BottomNavigation';
import KimsaToast from "@/components/common/KimsaToast";
import KimsaInstallToast from "@/components/common/KimsaInstallToast";
import OverlayReconnect from '@/components/common/OverlayReconnect';

import { LocalNotifications } from '@capacitor/local-notifications';

import firebase from 'firebase/app'
import 'firebase/auth'
import { db } from '@/utils/Firebase'
import { Device, Notifications } from "@/utils/Capacitor";
import { Storage } from "@/utils/Storage";
import { resolveNotificationRoute } from "@/utils/Routing";

import { mapState, mapMutations, mapActions } from 'vuex'
import DisabledAccount from "@/components/common/DisabledAccount";
import OverlayUpdate from "@/components/common/OverlayUpdate";
import UpdateApp from "@/components/common/UpdateApp";

export default {
    name: 'App',
    components: {
        UpdateApp,
        OverlayUpdate,
        DisabledAccount,
        Toolbar,
        DesktopToolbar,
        NavigationDrawer,
        BottomNavigation,
        KimsaToast,
        KimsaInstallToast,
        OverlayReconnect,
    },
    data() {
        return {
            needUpdateApp: false,
        }
    },
    computed: {
        ...mapState(['firstLoadApp', 'config']),
        meta(vm = this) {
            return vm.$route.meta
        },
        drawer(vm = this) {
            return !!vm.meta.drawer
        },
        toolbar(vm = this) {
            return !!vm.meta.toolbar
        },
        footer(vm = this) {
            return !!vm.meta.footer
        },
        desktopToolbar(vm = this) {
            return !!vm.meta.desktopToolbar
        },
        discordWebhook() {
            return process.env.VUE_APP_DISCORD_WEBHOOK || null
        },
        fullWidth(vm = this) {
            let routes = ['login', 'register', 'select-profile', 'basic-info', 'coach-company-invitation']
            return routes.includes(vm.$route.name)
        },
        noPaddingRoutes(vm = this) {
            let routes = ['student.coaches']
            return routes.includes(vm.$route.name)
        },
        acceptAccessRoutes(vm = this) {
            let routes = ['splash', 'coach.credit-card', 'coach.select-plan', 'company.credit-card', 'company.select-plan']
            return routes.includes(vm.$route.name)
        },
        hasPaymentFailed(vm = this) {
            let accountType = vm.account?.accountType || ''
            if (accountType !== 'coach' && accountType !== 'company') return false
            if (!Boolean(vm.account?.stripeAccount?.currentSubscription?.paymentFailed)) return false
            let date = vm.account?.stripeAccount?.currentSubscription?.nextPaymentAttempt || null
            if (!date || (date && !vm.$moment(date * 1000).isBefore())) return false
            if (Boolean(vm.account?.stripeAccount?.currentSubscription?.adminPass)) return false
            return true
        },
        currentVersionApp(vm = this) {
            return vm.config?.version?.coaches || null
        }
    },
    async created() {
        const vm = this
        await vm.checkVersionUpdate()
        await firebase.auth().onAuthStateChanged(async user => {
            console.log('auth status changed', user)
            if (user) {
                await vm.initUser(user)
                vm.$nextTick(() => {
                    setTimeout(async function () {
                        await Notifications.requestPermissions()
                        await Notifications.register(async token => {
                            if (vm.currentUser && vm.account?.pushToken === token) return
                            await db.collection('users')
                                .doc(vm.currentUser.uid)
                                .update({
                                    pushToken: token
                                })
                        }, (notification) => {
                            if (notification.actionId === 'tap' && notification.notification?.data?.path) {
                                resolveNotificationRoute(notification.notification.data.path);
                            }
                        }, (notification) => {
                            if (notification.data?.assignedRoutine) {
                                let assignedRoutine = JSON.parse(notification.data.assignedRoutine)
                                LocalNotifications.schedule({
                                    notifications: [{
                                        title: vm.appName,
                                        body: vm.$t('common.have_routine_to_complete'),
                                        id: Date.now(),
                                        schedule: { at: vm.$moment(assignedRoutine.rawDate.full).add(7, 'h').toDate() },
                                        actionTypeId: "",
                                        extra: notification.data,
                                    }]
                                });
                            }
                        })
                    }, 2000)
                })
            } else {
                const credentials = await Storage.getCredentials()

                if (credentials && vm.firstLoadApp) {
                    await firebase.auth().signInWithEmailAndPassword(credentials.email, credentials.password).then(() => {
                        vm.initUser(firebase.auth().currentUser)
                    })
                } else {
                    await vm.setCurrentUser(null)
                    vm.setLoadingUser(false)
                }
            }
        })
        setTimeout(() => {
            vm.setFirstLoadApp()
        }, 1000)
    },
    methods: {
        ...mapMutations(['setCurrentUser', 'setLoadingUser', 'setFirstLoadApp']),
        ...mapActions(['initUser', 'bindConfig']),
        async checkVersionUpdate() {
            const vm = this
            if (await Device.isAndroid()) {
                if (!vm.config) await vm.bindConfig()
                vm.needUpdateApp = vm.currentVersionApp > vm.versionAppCode
            }
        }
    },
    watch: {
        async account(val) {
            const vm = this
            let subscription = val?.stripeAccount?.currentSubscription || null
            if (subscription) await vm.$store.dispatch('bindSubscription', subscription.id)
        },
        currentVersionApp() {
            this.checkVersionUpdate()
        }
    }
}
</script>

<style>
.no-padding {
    padding: 0;
}

.background-app {
    background: linear-gradient(180deg, #FFFFFF 0%, #F9F9F9 100%);
}

.bottom-row {
    flex: 1;
    padding: 5px;
    display: flex;
    flex-direction: row;
}

.bottom-button {
    flex: 1;
    min-height: 40px;
    z-index: 6;
}

.container-bottom-button {
    flex-direction: column;
    position: fixed;
    display: flex;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 5;
    padding: 10px;
    background-color: rgba(255, 255, 255, .9);
}

.divider-primary {
    height: 2px !important;
    max-height: 10px;
    background-color: var(--v-primary-base);
    margin-top: 10px;
    margin-bottom: 10px;
}

.logo-title {
    color: #078571;
    font-weight: bold;
    font-size: 30px;
}

.label {
    font-size: 15px;
    color: #707070;
}

::-webkit-scrollbar {
    width: 10px;
    height: 10px;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
}

::-webkit-scrollbar-thumb {
    background-color: var(--v-accent-base);
}
</style>
